import { BrandInterface, LocationPageInterface, LocationVarKey, LocationVarWithId } from "@swivl-mono/types";
import { SelectableOption, Spinner } from "@swivl-mono/ui";
import React from "react";

import { classNameForRow, useVisibility } from "../../../Hooks/WorksheetRow.Hooks";
import { useMergedLocationVariables } from "../../../Hooks/useLocationVariables";
import { AlertsModel } from "../../../Models/Notifications/Alerts.Model";
import LocationVarsModel from "../../../Models/Variables/LocationVars.Model";
import { WorksheetComponentDropdown } from "../Components/Dropdown/WorksheetComponent.Dropdown";
import { WorksheetComponentSwitch } from "../Components/WorksheetComponent/WorksheetComponent.Switch";
import { WorksheetComponentText } from "../Components/WorksheetComponent/WorksheetComponent.Text";
import { WorksheetCondition } from "../Types/Worksheet.Interface";
import { WorksheetComponentTextAreaRow } from "../WorksheetComponentTextAreaRow/WorksheetComponentTextAreaRow";
import "./WorksheetComponent.Component.scss";
import { set } from "react-datepicker/dist/date_utils";

export enum WorksheetComponentType {
  SINGLE_LINE = "single line",
  PASSWORD = "password",
  MULTI_LINE = "multi line",
  SWITCH = "switch",
  DROPDOWN = "dropdown",
  TEXT = "text",
  SECTION_HEADER = "section header",
  SECTION = "section",
  PAGE_HEADER = "page header",
  INDENT = "indent",
  TEXT_AREA = "text area",
}

export interface WorksheetComponentProps extends LocationPageInterface {
  brand?: BrandInterface;
  children?: any;
  disableReverting?: boolean;
  enableSwitchVar?: LocationVarKey;
  hideIf?: WorksheetCondition;
  indentLevel?: number;
  isLoading?: boolean;
  keyName?: LocationVarKey;
  label?: string;
  marginBottom?: boolean;
  marginTop?: boolean;
  options?: SelectableOption[];
  subheading?: string;
  shouldSaveCallback?: (value: string) => Promise<boolean>;
}

export interface InternalWorksheetComponentProps extends WorksheetComponentProps {
  componentType: WorksheetComponentType;
}
export function WorksheetComponent(props: InternalWorksheetComponentProps) {
  const { keyName, enableSwitchVar } = props;
  const [isLoadingInternal, setIsLoading] = React.useState(false);
  const isLoading = props.isLoading || isLoadingInternal;
  const varsToAccess = keyName ? (enableSwitchVar ? [keyName, enableSwitchVar] : [keyName]) : [];
  const vars = useMergedLocationVariables(varsToAccess, props.groupId, props.locationId);
  const variable = vars && keyName ? vars[keyName] : undefined;
  const switchVariable = vars && enableSwitchVar ? vars[enableSwitchVar] : undefined;

  const visibility = useVisibility(props);

  const shouldSaveCallbackInternal = props.shouldSaveCallback ? async (value: string) => {
    const shouldSave = await props.shouldSaveCallback!(value);


    if (!shouldSave) {
      setIsLoading(false);
      return false;
    }
    return true;
  } : undefined;

  const saveVariable = async (value: string) => {
    if (!variable) {
      return;
    }
    if (!variable.id) {
      return;
    }

    setIsLoading(true);
    await LocationVarsModel.saveVariable(variable.id, value);
    setIsLoading(false);
  };

  const saveSwitchVariable = async (value: string) => {
    if (!switchVariable) {
      return;
    }
    if (!switchVariable.id) {
      return;
    }

    setIsLoading(true);

    await LocationVarsModel.saveVariable(switchVariable.id, value);

    setIsLoading(false);
  };

  const deleteVariable = async () => {
    if (!variable) {
      return;
    }
    if (!variable.id) {
      return;
    }
    setIsLoading(true);

    if (switchVariable && switchVariable.id) {
      await LocationVarsModel.deleteVariable(switchVariable as LocationVarWithId);
    }
    LocationVarsModel.deleteVariable(variable as LocationVarWithId);
    setIsLoading(false);
  };

  const customize = async () => {
    if (!variable || !keyName) {
      return;
    }
    setIsLoading(true);
    await LocationVarsModel.createVariable(keyName, variable?.value, props.groupId, props.locationId);
    if (props.enableSwitchVar) {
      await LocationVarsModel.createVariable(props.enableSwitchVar, switchVariable?.value || "0", props.groupId, props.locationId);
    }
    setIsLoading(false);
  };

  if (keyName && !variable) {
    return <Spinner />;
  }

  if (!keyName) {
    if (props.componentType === WorksheetComponentType.SECTION_HEADER) {
      return (
        <div className={classNameForRow("SectionHeader", props, visibility)}>
          {props.children}
          {props.subheading && <div className="SectionHeader__subheading">{props.subheading}</div>}
        </div>
      );
    }
    if (props.componentType === WorksheetComponentType.SECTION) {
      return <div className={classNameForRow("WorksheetSection", props, visibility)}>{props.children}</div>;
    }
    if (props.componentType === WorksheetComponentType.INDENT) {
      const indentLevel = props.indentLevel || 2;
      return <div className={`Indent indent-${indentLevel}`}>{props.children}</div>;
    }

    if (props.componentType === WorksheetComponentType.PAGE_HEADER) {
      return (
        <div className={classNameForRow("PageHeader", props, visibility)}>
          {props.children}
          {props.subheading && <div className="subheading">{props.subheading}</div>}
        </div>
      );
    }

    if (props.componentType === WorksheetComponentType.TEXT) {
      return <div className={classNameForRow("TextRow", props, visibility)}>{props.children}</div>;
    }
    return;
  }

  if (!variable) {
    return <Spinner />;
  }

  if (props.componentType === WorksheetComponentType.SWITCH) {
    return (
      <WorksheetComponentSwitch
        className={classNameForRow("SwitchRow", props, visibility)}
        key={"s_" + variable.key + "_" + props.path}
        variable={variable}
        isLoading={isLoading}
        customize={customize}
        deleteVariable={deleteVariable}
        saveVariable={saveVariable}
        label={props.label}
        type={props.type}
        isEditing={props.isEditing}
      />
    );
  }

  if (props.componentType === WorksheetComponentType.DROPDOWN) {
    return (
      <WorksheetComponentDropdown
        className={classNameForRow("ddrow", props, visibility)}
        key={"d_" + variable.key + "_" + props.path}
        variable={variable}
        options={props.options}
        isLoading={isLoading}
        customize={customize}
        deleteVariable={deleteVariable}
        saveVariable={saveVariable}
        label={props.label}
        type={props.type}
        isEditing={props.isEditing}
        controlType={"textarea"}
      />
    );
  }

  if (props.componentType === WorksheetComponentType.TEXT_AREA) {
    return (
      <WorksheetComponentTextAreaRow
        key={"m_" + variable.key + "_" + props.path}
        variable={variable}
        className={classNameForRow("TextAreaRow group", props, visibility)}
        isLoading={isLoading}
        customize={customize}
        deleteVariable={deleteVariable}
        enableSwitchVar={props.enableSwitchVar}
        switchVariable={switchVariable}
        saveVariable={saveVariable}
        saveSwitchVar={saveSwitchVariable}
        label={props.label}
        type={props.type}
        isEditing={props.isEditing}
        controlType={"textarea"}
        propsForSwitch={props}
        visibility={visibility}
      />
    );
  }

  if (props.componentType === WorksheetComponentType.MULTI_LINE) {
    return (
      <WorksheetComponentText
        className={classNameForRow("TextRow", props, visibility)}
        key={"m_" + variable.key + "_" + props.path}
        variable={variable}
        isLoading={isLoading}
        customize={customize}
        deleteVariable={deleteVariable}
        saveVariable={saveVariable}
        label={props.label}
        type={props.type}
        isEditing={props.isEditing}
        controlType={"textarea"}
        shouldSaveCallback={shouldSaveCallbackInternal}
      />
    );
  }

  if (props.componentType === WorksheetComponentType.PASSWORD) {
    return (
      <WorksheetComponentText
        className={classNameForRow("TextRow", props, visibility)}
        key={"t_" + variable.key + "_" + props.path}
        variable={variable}
        isLoading={isLoading}
        customize={customize}
        deleteVariable={deleteVariable}
        saveVariable={saveVariable}
        label={props.label}
        type={props.type}
        isEditing={props.isEditing}
        controlType={"password"}
        shouldSaveCallback={shouldSaveCallbackInternal}
      />
    );
  }
  return (
    <WorksheetComponentText
      className={classNameForRow("TextRow", props, visibility)}
      key={"t_" + variable.key + "_" + props.path}
      variable={variable}
      isLoading={isLoading}
      customize={customize}
      deleteVariable={deleteVariable}
      saveVariable={saveVariable}
      label={props.label}
      type={props.type}
      isEditing={props.isEditing}
      controlType={"input"}
      shouldSaveCallback={shouldSaveCallbackInternal}
    />
  );
}
